import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AcademicCapIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { useData } from '../../context/DataContext';
import {
  calculateMultiPortfolioGoalValue,
  convertCurrency,
} from '../../utils/calculations';
import { formatCurrency } from '../../utils/formatters';
import SchoolFeePlanModal from './SchoolFeePlanModal';

const SchoolFeesPage = () => {
  const navigate = useNavigate();
  const { schoolFeePlans, goals, portfolios, exchangeRates, settings } = useData();
  const [modalVisible, setModalVisible] = useState(false);

  // Enrich plans with computed data
  const enrichedPlans = useMemo(() => {
    return schoolFeePlans.map(plan => {
      const linkedGoal = plan.linkedGoalId ? goals.find(g => g.id === plan.linkedGoalId) : null;
      const totalFees = (plan.feeSchedule || []).reduce((s, f) => s + f.annualFee, 0);
      const firstYear = plan.feeSchedule?.[0]?.startYear;
      const lastYear = plan.feeSchedule?.[plan.feeSchedule.length - 1]?.endYear;

      let currentSavings = 0;
      if (linkedGoal) {
        const goalValue = calculateMultiPortfolioGoalValue(linkedGoal, portfolios, exchangeRates);
        currentSavings = convertCurrency(goalValue, linkedGoal.currency || 'GBP', plan.currency || 'GBP', exchangeRates);
      }

      const savingsPercent = totalFees > 0 ? Math.min(100, (currentSavings / totalFees) * 100) : 0;

      return {
        ...plan,
        linkedGoal,
        totalFees,
        firstYear,
        lastYear,
        currentSavings,
        savingsPercent,
      };
    });
  }, [schoolFeePlans, goals, portfolios, exchangeRates]);

  const totalAllFees = enrichedPlans.reduce((s, p) => s + p.totalFees, 0);
  const totalAllSavings = enrichedPlans.reduce((s, p) => s + p.currentSavings, 0);

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <AcademicCapIcon className="w-7 h-7 text-blue-600" />
          <div>
            <h1 className="text-xl font-bold text-gray-900">School Fees Planner</h1>
            <p className="text-sm text-gray-500">Plan and track school fee payments for your children</p>
          </div>
        </div>
        <button
          onClick={() => setModalVisible(true)}
          className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
        >
          <PlusIcon className="w-4 h-4" />
          Add Child
        </button>
      </div>

      {/* Summary */}
      {enrichedPlans.length > 0 && (
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-3">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider">Total Fees (All Children)</p>
            <p className="text-lg font-bold text-gray-900">{formatCurrency(totalAllFees, settings.displayCurrency, 0)}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-3">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider">Total Savings</p>
            <p className="text-lg font-bold text-gray-900">{formatCurrency(totalAllSavings, settings.displayCurrency, 0)}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-3">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider">Coverage</p>
            <p className={`text-lg font-bold ${totalAllSavings >= totalAllFees ? 'text-green-700' : 'text-amber-600'}`}>
              {totalAllFees > 0 ? `${Math.min(100, (totalAllSavings / totalAllFees * 100)).toFixed(0)}%` : '–'}
            </p>
          </div>
        </div>
      )}

      {/* Plan Cards */}
      {enrichedPlans.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {enrichedPlans.map(plan => (
            <div
              key={plan.id}
              onClick={() => navigate(`/school-fees/${plan.id}`)}
              className="bg-white rounded-xl border border-gray-200 p-4 cursor-pointer hover:border-gray-300 hover:shadow-sm transition-all"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <AcademicCapIcon className="w-5 h-5 text-blue-600" />
                  <h3 className="text-sm font-semibold text-gray-900">{plan.childName}</h3>
                </div>
                <span className="text-xs text-gray-400">
                  {plan.firstYear}–{plan.lastYear}
                </span>
              </div>

              {plan.linkedGoal && (
                <p className="text-xs text-gray-500 mb-2">
                  Goal: <span className="font-medium text-gray-700">{plan.linkedGoal.name}</span>
                </p>
              )}

              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-500">Total fees</span>
                <span className="text-sm font-bold text-gray-900">{formatCurrency(plan.totalFees, plan.currency, 0)}</span>
              </div>

              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-500">Current savings</span>
                <span className="text-sm font-semibold text-gray-700">{formatCurrency(plan.currentSavings, plan.currency, 0)}</span>
              </div>

              {/* Progress bar */}
              <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    plan.savingsPercent >= 100 ? 'bg-green-500' :
                    plan.savingsPercent >= 50 ? 'bg-blue-500' :
                    'bg-amber-500'
                  }`}
                  style={{ width: `${plan.savingsPercent}%` }}
                />
              </div>
              <p className="text-[10px] text-gray-400 mt-1 text-right">
                {plan.savingsPercent.toFixed(0)}% of total fees saved
              </p>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <AcademicCapIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-600 mb-2">No school fee plans yet</p>
          <p className="text-xs text-gray-400 mb-4">Add a child to start planning for their education fees</p>
          <button
            onClick={() => setModalVisible(true)}
            className="px-4 py-2 text-white rounded-lg font-medium text-sm bg-blue-600 hover:bg-blue-700"
          >
            Add Your First Child
          </button>
        </div>
      )}

      {/* Modal */}
      <SchoolFeePlanModal
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
      />
    </div>
  );
};

export default SchoolFeesPage;
